/**
 * @fileoverview Live Report Outline Animation
 *
 * Purpose: A stylised, looping recreation of Morgan turning connected matter
 * data into a living report outline, for the homepage feature card ("From raw
 * data (in any form) to clear decisions in minutes").
 *
 * It tells a three-beat story:
 *
 *  1. Syncing    - Morgan syncs the matter's source systems one by one
 *                  (SharePoint, Outlook, Xero, InfoTrack) — pulling documents,
 *                  email, ledgers and searches in whatever form they arrive.
 *  2. Generating - a stylised report overview is generated section by section,
 *                  in Morgan's format — Overview, then table-style sections for
 *                  Estimated Outcome, Assets, Liabilities and Preferred
 *                  Creditors (each with a header total and value rows).
 *  3. Live       - the finished overview stays on screen and auto-scrolls
 *                  through its tables.
 *
 * This is a marketing stylisation — no live data, intentionally abstract so it
 * doesn't give the real UI/UX away — sized to the feature-card container. It
 * reuses Morgan's colour language (emerald success, primary-green document) and
 * recognisable brand marks for the synced integrations.
 *
 * Respects prefers-reduced-motion by rendering the static "live" outline.
 *
 * Used by: FeatureCardsSection (-sections.feature-cards.tsx)
 */

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/utils/misc";

// --- Brand marks -----------------------------------------------------------
// Recognisable logos for the synced integrations. SharePoint, Outlook and Xero
// use the official single-glyph brand marks (from Simple Icons) rendered in
// their brand colours; InfoTrack uses its official square site icon asset.

interface MarkProps {
  className?: string;
}

/** Microsoft SharePoint — official brand glyph in SharePoint teal. */
function SharePointMark({ className }: MarkProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="#036C70"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M24 13.5q0 1.242-.475 2.332-.474 1.09-1.289 1.904-.814.815-1.904 1.29-1.09.474-2.332.474-.762 0-1.523-.2-.106.997-.557 1.858-.451.862-1.154 1.494-.704.633-1.606.99-.902.358-1.91.358-1.09 0-2.045-.416-.955-.416-1.664-1.125-.709-.709-1.125-1.664Q6 19.84 6 18.75q0-.188.018-.375.017-.188.04-.375H.997q-.41 0-.703-.293T0 17.004V6.996q0-.41.293-.703T.996 6h3.54q.14-1.277.726-2.373.586-1.096 1.488-1.904Q7.652.914 8.807.457 9.96 0 11.25 0q1.395 0 2.625.533T16.02 1.98q.914.915 1.447 2.145T18 6.75q0 .188-.012.375-.011.188-.035.375 1.242 0 2.344.469 1.101.468 1.928 1.277.826.809 1.3 1.904Q24 12.246 24 13.5zm-12.75-12q-.973 0-1.857.34-.885.34-1.577.943-.691.604-1.154 1.43Q6.2 5.039 6.06 6h4.945q.41 0 .703.293t.293.703v4.945l.21-.035q.212-.75.61-1.424.399-.673.944-1.218.545-.545 1.213-.944.668-.398 1.43-.61.093-.503.093-.96 0-1.09-.416-2.045-.416-.955-1.125-1.664-.709-.709-1.664-1.125Q12.34 1.5 11.25 1.5zM6.117 15.902q.54 0 1.06-.111.522-.111.932-.37.41-.257.662-.679.252-.422.252-1.055 0-.632-.263-1.054-.264-.422-.662-.703-.399-.282-.856-.463l-.855-.34q-.399-.158-.662-.334-.264-.176-.264-.445 0-.2.14-.323.141-.123.335-.193.193-.07.404-.094.21-.023.351-.023.598 0 1.055.152.457.153.95.457V8.543q-.282-.082-.522-.14-.24-.06-.475-.1-.234-.041-.486-.059-.252-.017-.557-.017-.515 0-1.054.117-.54.117-.979.375-.44.258-.715.68-.275.421-.275 1.03 0 .598.263.997.264.398.663.68.398.28.855.474l.856.363q.398.17.662.358.263.187.263.457 0 .222-.123.351-.123.13-.31.2-.188.07-.393.087-.205.018-.369.018-.703 0-1.248-.234-.545-.235-1.107-.621v1.875q1.195.468 2.472.468zM11.25 22.5q.773 0 1.453-.293t1.19-.803q.51-.51.808-1.195.299-.686.299-1.459 0-.668-.223-1.277-.222-.61-.62-1.096-.4-.486-.95-.826-.55-.34-1.207-.48v1.933q0 .41-.293.703t-.703.293H7.57q-.07.375-.07.75 0 .773.293 1.459t.803 1.195q.51.51 1.195.803.686.293 1.459.293zM18 18q.926 0 1.746-.352.82-.351 1.436-.966.615-.616.966-1.43.352-.815.352-1.752 0-.926-.352-1.746-.351-.82-.966-1.436-.616-.615-1.436-.966Q18.926 9 18 9t-1.74.357q-.815.358-1.43.973t-.973 1.43q-.357.814-.357 1.74 0 .129.006.258t.017.258q.551.27 1.02.65t.838.855q.369.475.627 1.026.258.55.387 1.148Q17.18 18 18 18Z" />
    </svg>
  );
}

/** Microsoft Outlook — official brand glyph in Outlook blue. */
function OutlookMark({ className }: MarkProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="#0078D4"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z" />
    </svg>
  );
}

/** Xero — official brand glyph in Xero blue. */
function XeroMark({ className }: MarkProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="#13B5EA"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm6.585 14.655c-1.485 0-2.69-1.206-2.69-2.689 0-1.485 1.207-2.691 2.69-2.691 1.485 0 2.69 1.207 2.69 2.691s-1.207 2.689-2.69 2.689zM7.53 14.644c-.099 0-.192-.041-.267-.116l-2.043-2.04-2.052 2.047c-.069.068-.16.108-.258.108-.202 0-.368-.166-.368-.368 0-.099.04-.191.111-.263l2.04-2.05-2.038-2.047c-.075-.069-.113-.162-.113-.261 0-.203.166-.366.368-.366.098 0 .188.037.258.105l2.055 2.048 2.048-2.045c.069-.071.162-.108.26-.108.211 0 .375.165.375.366 0 .098-.029.188-.104.258l-2.056 2.055 2.055 2.051c.068.069.104.16.104.258 0 .202-.165.368-.365.368h-.01zm8.017-4.591c-.796.101-.882.476-.882 1.404v2.787c0 .202-.165.366-.366.366-.203 0-.367-.165-.368-.366v-4.53c0-.204.16-.366.362-.366.166 0 .316.125.346.289.27-.209.6-.317.93-.317h.105c.195 0 .359.165.359.368 0 .201-.164.352-.375.359 0 0-.09 0-.164.008l.053-.002zm-3.091 2.205H8.625c0 .019.003.037.006.057.02.105.045.211.083.31.194.531.765 1.275 1.829 1.29.33-.003.631-.086.9-.229.21-.12.391-.271.525-.428.045-.058.09-.112.12-.168.18-.229.405-.186.54-.083.164.135.18.391.045.57l-.016.016c-.21.27-.435.495-.689.66-.255.164-.525.284-.811.345-.33.09-.645.104-.975.06-1.095-.135-2.01-.93-2.28-2.01-.06-.21-.09-.42-.09-.645 0-.855.421-1.695 1.125-2.205.885-.615 2.085-.66 3-.075.63.405 1.035 1.021 1.185 1.771.075.419-.21.794-.734.81l.068-.046zm6.129-2.223c-1.064 0-1.931.865-1.931 1.931 0 1.064.866 1.931 1.931 1.931s1.931-.867 1.931-1.931c0-1.065-.866-1.933-1.931-1.933v.002zm0 2.595c-.367 0-.666-.297-.666-.666 0-.367.3-.665.666-.665.367 0 .667.299.667.665 0 .369-.3.667-.667.666zm-8.04-2.603c-.91 0-1.672.623-1.886 1.466v.03h3.776c-.203-.855-.973-1.494-1.891-1.494v-.002z" />
    </svg>
  );
}

/** InfoTrack — official square brand mark (local asset). */
function InfoTrackMark({ className }: MarkProps) {
  return (
    <img
      src="/logos/infotrack.png"
      alt=""
      aria-hidden="true"
      className={cn("rounded-[2px] object-contain", className)}
    />
  );
}

type BrandMark = (props: MarkProps) => JSX.Element;

interface SyncSource {
  id: string;
  name: string;
  /** What Morgan pulls from this system. */
  detail: string;
  /** Short summary that appears once synced. */
  synced: string;
  Mark: BrandMark;
}

/** Source systems Morgan syncs at the start of the matter. */
const SYNC_SOURCES: SyncSource[] = [
  {
    id: "sharepoint",
    name: "SharePoint",
    detail: "Bank statements & documents",
    synced: "142 files",
    Mark: SharePointMark,
  },
  {
    id: "outlook",
    name: "Outlook",
    detail: "Correspondence & email threads",
    synced: "318 emails",
    Mark: OutlookMark,
  },
  {
    id: "xero",
    name: "Xero",
    detail: "General & subsidiary ledgers",
    synced: "18 reports",
    Mark: XeroMark,
  },
  {
    id: "infotrack",
    name: "InfoTrack",
    detail: "ASIC, PPSR & title searches",
    synced: "9 searches",
    Mark: InfoTrackMark,
  },
] as const;

type SyncState = "pending" | "syncing" | "synced";

/** A single row inside a table-style section. */
interface OutlineRow {
  label: string;
  value: string;
  /** Optional muted secondary detail (e.g. a class or party type). */
  detail?: string;
}

interface OutlineSection {
  id: string;
  label: string;
  /** Figure shown in the section header (right-aligned total). */
  total?: string;
  /** Optional muted caption next to the total (e.g. "5 registrations"). */
  totalCaption?: string;
  /** Table rows. When present, the section renders as a table. */
  rows?: OutlineRow[];
  /** Number of stylised summary lines (used when the section has no rows). */
  lines?: number;
}

/**
 * The stylised report overview, in Morgan's format.
 *
 * Figures are stylised versions of the real platform tables (see the Assets and
 * Security Interests views): Assets group by type and total $125,000; the
 * Liabilities → Security Interests register totals $18,016 est. owed; the
 * Estimated Outcome waterfall ties those together into an estimated dividend.
 */
const OUTLINE: OutlineSection[] = [
  { id: "overview", label: "Overview", lines: 2 },
  {
    id: "outcome",
    label: "Estimated Outcome",
    total: "$124,720",
    totalCaption: "Practitioner shortfall",
  },
  {
    id: "assets",
    label: "Assets",
    total: "$125,000",
    rows: [
      { label: "Plant & Equipment", value: "$50,000" },
      { label: "Accounts Receivable", value: "$70,000" },
      { label: "Stock / Inventory", value: "$5,000" },
      { label: "Cash at Bank", value: "$0" },
    ],
  },
  {
    id: "liabilities",
    label: "Security Interests",
    total: "$18,016",
    totalCaption: "5 registrations",
    rows: [
      {
        label: "Toyota Finance Australia",
        value: "—",
        detail: "Motor vehicle",
      },
      { label: "National Australia Bank", value: "—", detail: "Motor vehicle" },
      { label: "GC Leasing Sydney", value: "—", detail: "Other goods" },
      { label: "Lumi Finance", value: "$18,016", detail: "ALLPAP" },
      { label: "PGH Bricks & Pavers", value: "—", detail: "Other goods" },
    ],
  },
  {
    id: "preferred",
    label: "Preferred Creditors",
    total: "$36,540",
    rows: [
      { label: "Wages & superannuation", value: "$24,300" },
      { label: "Annual leave", value: "$7,240" },
      { label: "Redundancy pay", value: "$5,000" },
    ],
  },
] as const;

/**
 * Estimated Outcome — bespoke layout that mirrors the platform's waterfall
 * running balance (ROCAP basis). Rendered specially (not as a generic
 * label/value table); the practitioner shortfall sits in the section header.
 */
const OUTCOME_ROWS = [
  { label: "Net Realisable Assets", value: "$0" },
  { label: "Appointment Costs", value: "$0", detail: "1 line" },
  { label: "Priority Creditors", value: "$0", detail: "1 line" },
  { label: "Unsecured Creditors", value: "-$124,720", detail: "5 lines" },
] as const;

const OUTCOME_FINAL = {
  label: "Estimated Outcome to Creditors",
  value: "-$124,720",
} as const;

// --- Timing (module scope so the progress line stays in sync with the run) ---
/** Gap between each integration starting to sync. */
const SYNC_ITEM_STAGGER_MS = 700;
/** How long an integration spends "syncing" before it lands on "synced". */
const SYNC_ITEM_DURATION_MS = 1100;
const SYNCING_END_MS =
  (SYNC_SOURCES.length - 1) * SYNC_ITEM_STAGGER_MS +
  SYNC_ITEM_DURATION_MS +
  650;
const SECTION_STAGGER_MS = 200;
const GENERATING_END_MS =
  SYNCING_END_MS + OUTLINE.length * SECTION_STAGGER_MS + 300;
/** How long the overview stays live (figures ticking) before looping. */
const LIVE_MS = 8600;
/** Total length of one play-through — drives the progress line + orchestrator. */
export const REPORT_OUTLINE_ANIMATION_CYCLE_MS = GENERATING_END_MS + LIVE_MS;

type Phase = "syncing" | "generating" | "live";

interface ReportOutlineAnimationProps {
  /**
   * When false, the animation rests on the static "live" outline (no playback,
   * no progress line). The parent orchestrator toggles this so only one card
   * animates at a time.
   */
  isActive?: boolean;
}

export function ReportOutlineAnimation({
  isActive = true,
}: ReportOutlineAnimationProps) {
  const reducedMotion = useReducedMotion() ?? false;

  const [phase, setPhase] = useState<Phase>("syncing");
  const [syncStates, setSyncStates] = useState<SyncState[]>(
    SYNC_SOURCES.map(() => "pending"),
  );
  const [visibleSections, setVisibleSections] = useState(0);
  // Bumped at the start of every play-through so the progress line restarts.
  const [cycle, setCycle] = useState(0);
  // How far the overview can scroll (content height − viewport height).
  const [scrollDistance, setScrollDistance] = useState(0);

  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  useEffect(() => {
    // Resting / static state: the fully generated, fully synced outline.
    if (reducedMotion || !isActive) {
      timers.current.forEach(clearTimeout);
      timers.current = [];
      setPhase("live");
      setSyncStates(SYNC_SOURCES.map(() => "synced"));
      setVisibleSections(OUTLINE.length);
      return;
    }

    const clearAll = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };

    const after = (ms: number, fn: () => void) => {
      timers.current.push(setTimeout(fn, ms));
    };

    const setSyncAt = (index: number, state: SyncState) => {
      setSyncStates((prev) => {
        const next = [...prev];
        next[index] = state;
        return next;
      });
    };

    const runCycle = () => {
      // Reset
      setPhase("syncing");
      setCycle((c) => c + 1);
      setSyncStates(SYNC_SOURCES.map(() => "pending"));
      setVisibleSections(0);

      // Beat 1: source systems sync one by one (pending → syncing → synced).
      SYNC_SOURCES.forEach((_, i) => {
        const start = i * SYNC_ITEM_STAGGER_MS;
        after(start, () => setSyncAt(i, "syncing"));
        after(start + SYNC_ITEM_DURATION_MS, () => setSyncAt(i, "synced"));
      });

      // Beat 2: overview generates section by section, in Morgan's format.
      after(SYNCING_END_MS, () => setPhase("generating"));
      OUTLINE.forEach((_, i) => {
        after(SYNCING_END_MS + i * SECTION_STAGGER_MS, () =>
          setVisibleSections(i + 1),
        );
      });

      // Beat 3: go live — the finished overview stays on screen (and scrolls).
      after(GENERATING_END_MS, () => setPhase("live"));

      // Loop.
      after(REPORT_OUTLINE_ANIMATION_CYCLE_MS, runCycle);
    };

    runCycle();
    return clearAll;
  }, [reducedMotion, isActive]);

  const playing = isActive && !reducedMotion;
  const syncing = phase === "syncing";
  const live = phase === "live";

  // Measure how far the overview overflows its viewport so the live beat can
  // auto-scroll through the full set of tables.
  useEffect(() => {
    if (syncing) return;
    const measure = () => {
      const vp = viewportRef.current;
      const ct = contentRef.current;
      if (!vp || !ct) return;
      setScrollDistance(Math.max(0, ct.scrollHeight - vp.clientHeight));
    };
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [syncing, visibleSections]);

  const canScroll = live && playing && scrollDistance > 0;

  return (
    <div className="absolute inset-0 overflow-hidden rounded-lg bg-white dark:bg-card">
      {/* Progress line — fills along the bottom while this animation plays */}
      {playing && (
        <motion.div
          key={cycle}
          className="absolute inset-x-0 bottom-0 z-50 h-[3px] bg-primary"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{
            duration: REPORT_OUTLINE_ANIMATION_CYCLE_MS / 1000,
            ease: "linear",
          }}
          style={{ originX: 0 }}
        />
      )}

      <div className="flex h-full w-full flex-col">
        {/* Title bar */}
        <div className="flex items-center gap-2 border-b border-primary/10 bg-primary/[0.03] px-3 py-2">
          <div className="flex gap-1.5">
            <span className="h-2 w-2 rounded-full bg-red-400/70" />
            <span className="h-2 w-2 rounded-full bg-amber-400/70" />
            <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
          </div>
          <span className="ml-1 text-[11px] font-semibold text-primary">
            {syncing ? "Sync Sources" : "Overview"}
          </span>
          {live ? (
            <span className="ml-auto flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              Live
            </span>
          ) : (
            <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-primary/55">
              <Loader2 className="h-3 w-3 animate-spin" />
              {syncing ? "Syncing data…" : "Generating overview…"}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-3">
          {syncing ? (
            /* Beat 1: syncing the source systems */
            <SyncPanel syncStates={syncStates} playing={playing} />
          ) : (
            /* Beats 2 & 3: the generated overview, in Morgan's format */
            <>
              {/* Synced source chips — continuity from the sync beat */}
              <div className="shrink-0">
                <div className="mb-1 flex items-center gap-1 text-[8.5px] font-semibold uppercase tracking-wider text-primary/40">
                  Synced sources
                </div>
                <div className="flex flex-wrap gap-1">
                  {SYNC_SOURCES.map((source) => {
                    const Mark = source.Mark;
                    return (
                      <span
                        key={source.id}
                        className="inline-flex items-center gap-1 rounded border border-primary/15 bg-primary/[0.06] px-1.5 py-0.5 text-[8.5px] font-medium text-primary"
                      >
                        <Mark className="h-2.5 w-2.5" />
                        {source.name}
                        <Check
                          className="h-2 w-2 text-emerald-600"
                          strokeWidth={3}
                        />
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Overview sections, in Morgan's format — auto-scrolls live */}
              <div
                ref={viewportRef}
                className="relative mt-2.5 min-h-0 flex-1 overflow-hidden rounded-md border border-primary/10 bg-primary/[0.02]"
              >
                <motion.div
                  ref={contentRef}
                  className="flex flex-col gap-2 p-2"
                  animate={
                    canScroll ? { y: [0, -scrollDistance, 0] } : { y: 0 }
                  }
                  transition={
                    canScroll
                      ? {
                          duration: 9,
                          repeat: Infinity,
                          ease: "easeInOut",
                          times: [0, 0.5, 1],
                        }
                      : { duration: 0.3 }
                  }
                >
                  {OUTLINE.map((section, i) => {
                    const isVisible = i < visibleSections;
                    return (
                      <motion.div
                        key={section.id}
                        initial={false}
                        animate={
                          isVisible
                            ? { opacity: 1, y: 0 }
                            : { opacity: 0, y: 6 }
                        }
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden rounded-md border border-primary/10 bg-white dark:bg-card"
                      >
                        {/* Section header — number, label, total */}
                        <div className="flex items-center gap-1.5 border-b border-primary/10 bg-primary/[0.03] px-2 py-1.5">
                          <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] bg-primary/10 text-[7px] font-bold text-primary">
                            {i + 1}
                          </span>
                          <span className="truncate text-[9.5px] font-semibold text-foreground">
                            {section.label}
                          </span>

                          {section.total ? (
                            <span className="ml-auto flex shrink-0 items-baseline gap-1">
                              {section.totalCaption ? (
                                <span className="text-[7.5px] font-medium text-primary/40">
                                  {section.totalCaption}
                                </span>
                              ) : null}
                              <span
                                className={cn(
                                  "font-mono text-[9.5px] font-bold tabular-nums",
                                  section.id === "outcome"
                                    ? "text-red-600"
                                    : "text-primary",
                                )}
                              >
                                {section.total}
                              </span>
                            </span>
                          ) : null}
                        </div>

                        {/* Body: outcome scenarios, table rows, or summary */}
                        {section.id === "outcome" ? (
                          <div className="p-2">
                            {/* Waterfall (ROCAP basis) */}
                            <div className="overflow-hidden rounded border border-primary/10">
                              <div className="flex items-center justify-between bg-primary/[0.03] px-2 py-0.5">
                                <span className="text-[6.5px] font-semibold uppercase tracking-wider text-primary/40">
                                  Description
                                </span>
                                <span className="text-[6.5px] font-semibold uppercase tracking-wider text-primary/40">
                                  ROCAP
                                </span>
                              </div>
                              <div className="divide-y divide-primary/[0.06]">
                                {OUTCOME_ROWS.map((row) => (
                                  <div
                                    key={row.label}
                                    className="flex items-center gap-2 px-2 py-1"
                                  >
                                    <span className="truncate text-[8.5px] text-foreground/70">
                                      {row.label}
                                    </span>
                                    {"detail" in row && row.detail ? (
                                      <span className="shrink-0 rounded bg-primary/[0.06] px-1 py-px text-[7px] font-medium text-primary/45">
                                        {row.detail}
                                      </span>
                                    ) : null}
                                    <span
                                      className={cn(
                                        "ml-auto shrink-0 font-mono text-[8.5px] tabular-nums",
                                        row.value.startsWith("-")
                                          ? "text-red-600"
                                          : "text-foreground/80",
                                      )}
                                    >
                                      {row.value}
                                    </span>
                                  </div>
                                ))}
                                <div className="flex items-center justify-between bg-primary/[0.03] px-2 py-1">
                                  <span className="truncate text-[8.5px] font-semibold text-foreground">
                                    {OUTCOME_FINAL.label}
                                  </span>
                                  <span className="ml-auto shrink-0 font-mono text-[9px] font-bold tabular-nums text-red-600">
                                    {OUTCOME_FINAL.value}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : section.rows ? (
                          <div className="divide-y divide-primary/[0.06]">
                            {section.rows.map((row) => (
                              <div
                                key={row.label}
                                className="flex items-center gap-2 px-2 py-1"
                              >
                                <span className="truncate text-[8.5px] text-foreground/70">
                                  {row.label}
                                </span>
                                {row.detail ? (
                                  <span className="shrink-0 rounded bg-primary/[0.06] px-1 py-px text-[7px] font-medium text-primary/45">
                                    {row.detail}
                                  </span>
                                ) : null}
                                <span className="ml-auto shrink-0 font-mono text-[8.5px] tabular-nums text-foreground/80">
                                  {row.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-1 px-2 py-1.5">
                            {Array.from({ length: section.lines ?? 2 }).map(
                              (_, li) => (
                                <div
                                  key={li}
                                  className={cn(
                                    "h-1.5 rounded-full bg-primary/10",
                                    li === 0 ? "w-full" : "w-3/4",
                                  )}
                                />
                              ),
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </motion.div>

                {/* Soft top/bottom fades to sell the scroll */}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-white to-transparent dark:from-card" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white to-transparent dark:from-card" />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * SyncPanel — the first beat: Morgan syncing its source systems.
 *
 * Each integration row moves through pending → syncing → synced, with a thin
 * progress bar filling while it syncs and a short summary appearing once done.
 */
function SyncPanel({
  syncStates,
  playing,
}: {
  syncStates: SyncState[];
  playing: boolean;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-1.5 flex items-center gap-1 text-[8.5px] font-semibold uppercase tracking-wider text-primary/40">
        Connecting source systems
      </div>
      <div className="flex flex-1 flex-col justify-center gap-2">
        {SYNC_SOURCES.map((source, i) => {
          const state = syncStates[i] ?? "pending";
          const Mark = source.Mark;
          const isSyncing = state === "syncing";
          const isSynced = state === "synced";
          return (
            <div
              key={source.id}
              className={cn(
                "relative overflow-hidden rounded-md border px-2.5 py-2 transition-colors",
                isSynced
                  ? "border-emerald-200 bg-emerald-50/40 dark:border-emerald-500/25 dark:bg-emerald-500/10"
                  : isSyncing
                    ? "border-primary/20 bg-primary/[0.04]"
                    : "border-primary/10 bg-white dark:bg-card",
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border bg-white transition-opacity dark:bg-card",
                    state === "pending"
                      ? "border-primary/10 opacity-50"
                      : "border-primary/10 opacity-100",
                  )}
                >
                  <Mark className="h-3.5 w-3.5" />
                </span>

                <div className="min-w-0">
                  <div
                    className={cn(
                      "text-[10px] font-semibold leading-tight",
                      state === "pending"
                        ? "text-primary/45"
                        : "text-foreground",
                    )}
                  >
                    {source.name}
                  </div>
                  <div className="truncate text-[8px] leading-tight text-primary/45">
                    {source.detail}
                  </div>
                </div>

                {/* Right-side status */}
                <div className="ml-auto shrink-0">
                  {isSynced ? (
                    <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[8px] font-semibold text-emerald-700">
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      {source.synced}
                    </span>
                  ) : isSyncing ? (
                    <span className="inline-flex items-center gap-1 text-[8px] font-medium text-primary/55">
                      <Loader2 className="h-2.5 w-2.5 animate-spin" />
                      Syncing…
                    </span>
                  ) : (
                    <span className="text-[8px] font-medium text-primary/30">
                      Waiting
                    </span>
                  )}
                </div>
              </div>

              {/* Progress bar — fills while syncing, full once synced */}
              <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-primary/10">
                <motion.div
                  className={cn(
                    "h-full rounded-full",
                    isSynced ? "bg-emerald-500" : "bg-primary",
                  )}
                  initial={false}
                  animate={{
                    width: isSynced ? "100%" : isSyncing ? "85%" : "0%",
                  }}
                  transition={{
                    duration:
                      isSyncing && playing ? SYNC_ITEM_DURATION_MS / 1000 : 0.3,
                    ease: "easeOut",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
